import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, X, MailQuestion, PauseCircle, PlayCircle, Search, Pencil, Upload, FileDown, Trash2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { SupplierStatusBadge, SupplierForm } from "./Suppliers";
import { AuditBadge } from "./AuditLog";

const field = (label, value, testid) => (
  <div data-testid={testid}>
    <div className="text-[11px] font-[Poppins] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    <div className="mt-0.5 text-[13.5px] text-[var(--pg-navy)] whitespace-pre-wrap">{value || <span className="text-slate-300">Not provided</span>}</div>
  </div>
);

function Card({ title, children, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid={testid}>
      <h3 className="font-[Poppins] font-bold text-[15px] text-[var(--pg-navy)] mb-4">{title}</h3>
      {children}
    </div>
  );
}

function ActionButtons({ s, onAction }) {
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (action, withNote = false) => {
    if (withNote && noteFor !== action) { setNoteFor(action); setNote(""); return; }
    setBusy(true);
    try {
      await api.post(`/admin/suppliers/${s.id}/review-action`, { action, note });
      toast.success({ approve: "Supplier approved — portal access unlocked", reject: "Supplier rejected",
        request_info: "Information request recorded (email queued)", suspend: "Supplier suspended",
        reactivate: "Supplier reactivated", start_review: "Marked under review" }[action]);
      setNoteFor(null);
      onAction();
    } catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); }
    setBusy(false);
  };
  const btn = "inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg disabled:opacity-60";
  const reviewing = ["PENDING", "UNDER_REVIEW"].includes(s.status);
  const approvedish = ["ACTIVE", "PREFERRED"].includes(s.status);
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {s.status === "PENDING" && (
          <button data-testid="supplier-start-review" onClick={() => run("start_review")} disabled={busy}
            className={`${btn} bg-cyan-100 text-cyan-700 hover:bg-cyan-200`}><Search size={14} /> Start Review</button>
        )}
        {reviewing && (
          <>
            <button data-testid="supplier-approve" onClick={() => run("approve")} disabled={busy}
              className={`${btn} bg-green-600 text-white hover:bg-green-700`}><Check size={14} /> Approve</button>
            <button data-testid="supplier-reject" onClick={() => run("reject", true)} disabled={busy}
              className={`${btn} bg-red-100 text-red-600 hover:bg-red-200`}><X size={14} /> Reject</button>
            <button data-testid="supplier-request-info" onClick={() => run("request_info", true)} disabled={busy}
              className={`${btn} bg-slate-100 text-slate-600 hover:bg-slate-200`}><MailQuestion size={14} /> Request More Info</button>
          </>
        )}
        {approvedish && (
          <button data-testid="supplier-suspend" onClick={() => run("suspend", true)} disabled={busy}
            className={`${btn} bg-orange-100 text-orange-700 hover:bg-orange-200`}><PauseCircle size={14} /> Suspend</button>
        )}
        {["SUSPENDED", "REJECTED", "INACTIVE", "DO_NOT_USE", "PROSPECT"].includes(s.status) && (
          <button data-testid="supplier-reactivate" onClick={() => run("reactivate")} disabled={busy}
            className={`${btn} bg-green-100 text-green-700 hover:bg-green-200`}><PlayCircle size={14} /> {s.status === "SUSPENDED" ? "Reactivate" : "Approve / Activate"}</button>
        )}
      </div>
      {noteFor && (
        <div className="mt-3 flex flex-wrap items-center gap-2 bg-slate-50 rounded-lg p-3" data-testid="supplier-action-note">
          <input data-testid="supplier-action-note-input" className="pg-input !py-2 flex-1 min-w-[240px] text-[13px]" autoFocus
            placeholder={noteFor === "request_info" ? "What do you need from the supplier? (emailed to them)" : "Reason (recorded, emailed to supplier)"}
            value={note} onChange={(e) => setNote(e.target.value)} maxLength={1500} />
          <button data-testid="supplier-action-note-confirm" onClick={() => run(noteFor)} disabled={busy}
            className={`${btn} bg-[var(--pg-navy)] text-white hover:bg-[var(--pg-accent)]`}>Confirm {noteFor.replace(/_/g, " ")}</button>
          <button onClick={() => setNoteFor(null)} className="text-[12.5px] font-[Poppins] font-semibold text-slate-500 px-2">Cancel</button>
        </div>
      )}
    </div>
  );
}

function Documents({ s, onChange }) {
  const inputRef = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/admin/suppliers/${s.id}/documents`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };
  const download = async (d) => {
    try {
      const res = await api.get(`/admin/supplier-documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const remove = async (d) => {
    try { await api.delete(`/admin/supplier-documents/${d.id}`); toast.success("Document removed"); onChange(); }
    catch { toast.error("Delete failed"); }
  };
  return (
    <Card title={`Documents (${(s.documents || []).length})`} testid="supplier-documents-card">
      {(s.documents || []).length === 0 && <p className="text-[13px] text-slate-400" data-testid="supplier-docs-empty">No documents uploaded (W-9, capability statement, insurance certificate...).</p>}
      <div className="space-y-2">
        {(s.documents || []).map((d) => (
          <div key={d.id} className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-3 py-2 text-[13px]" data-testid={`supplier-doc-${d.id}`}>
            <span className="truncate text-[var(--pg-navy)] font-medium">{d.original_filename}</span>
            <span className="flex-shrink-0">
              <button data-testid={`supplier-doc-download-${d.id}`} onClick={() => download(d)} className="text-slate-400 hover:text-[var(--pg-accent)] p-1"><FileDown size={14} /></button>
              <button data-testid={`supplier-doc-delete-${d.id}`} onClick={() => remove(d)} className="text-slate-400 hover:text-red-500 p-1"><Trash2 size={14} /></button>
            </span>
          </div>
        ))}
      </div>
      <input ref={inputRef} type="file" className="hidden" onChange={upload} accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.zip,.txt" />
      <button data-testid="supplier-doc-upload" onClick={() => inputRef.current?.click()}
        className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">
        <Upload size={14} /> Upload Document
      </button>
    </Card>
  );
}

export default function SupplierDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [editing, setEditing] = useState(false);
  const load = useCallback(() => {
    api.get(`/admin/suppliers/${id}`).then((r) => setS(r.data)).catch(() => setS(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (s === null) return <div className="text-slate-500 p-8" data-testid="supplier-detail-loading">Loading supplier...</div>;
  if (s === false) return <div className="p-8 text-red-600" data-testid="supplier-detail-error">Supplier not found. <Link to="/admin/suppliers" className="underline">Back to list</Link></div>;

  return (
    <div data-testid="admin-supplier-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <Link to="/admin/suppliers" data-testid="supplier-back-link" className="text-slate-500 hover:text-[var(--pg-navy)]"><ArrowLeft size={20} /></Link>
          <div>
            <h1 className="font-[Poppins] font-bold text-[24px] text-[var(--pg-navy)]">{s.company_name}</h1>
            <p className="text-[12.5px] text-slate-500">Registered {(s.created_at || "").slice(0, 10)}{s.portal_account ? ` · portal account: ${s.portal_account.email}` : " · no portal account"}</p>
          </div>
          <SupplierStatusBadge status={s.status} />
        </div>
        <button data-testid="supplier-detail-edit" onClick={() => setEditing((v) => !v)}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">
          <Pencil size={14} /> Edit Details
        </button>
      </div>

      <div className="mt-5">
        <ActionButtons s={s} onAction={load} />
      </div>

      {(s.missing_info || []).length > 0 && (
        <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-5" data-testid="supplier-missing-info">
          <div className="flex items-center gap-2 font-[Poppins] font-bold text-[14px] text-amber-800"><AlertTriangle size={16} /> Missing information</div>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {s.missing_info.map((m) => (
              <span key={m} className="text-[12px] font-medium text-amber-800 bg-white border border-amber-200 rounded-md px-2.5 py-1">{m}</span>
            ))}
          </div>
          <p className="mt-2.5 text-[12.5px] text-amber-700">You can still approve — or use "Request More Info" to email the supplier this list.</p>
        </div>
      )}

      {editing && <div className="mt-5"><SupplierForm initial={s} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} /></div>}

      <div className="mt-5 grid lg:grid-cols-2 gap-5">
        <Card title="Contact" testid="supplier-contact-card">
          <div className="grid sm:grid-cols-2 gap-4">
            {field("Contact Name", s.contact_name)}
            {field("Email", s.email)}
            {field("Phone", s.phone)}
            {field("Website", s.website)}
            {field("Address", [s.address, s.state, s.country].filter(Boolean).join(", "))}
          </div>
        </Card>
        <Card title="Business Information" testid="supplier-business-card">
          <div className="grid sm:grid-cols-2 gap-4">
            {field("Type", s.supplier_type)}
            {field("Geographic Coverage", s.geo_coverage)}
            {field("Gov Contracting Capable", s.gov_capable ? "Yes" : "No")}
            {field("Rating", `${s.rating || 0} / 5`)}
            {field("UEI", s.uei, "supplier-field-uei")}
            {field("CAGE Code", s.cage_code, "supplier-field-cage")}
          </div>
        </Card>
        <Card title="Products, Codes & Certifications" testid="supplier-capabilities-card">
          <div className="grid gap-4">
            {field("Products / Service Categories", s.products)}
            {field("NAICS Codes", s.naics_codes, "supplier-field-naics")}
            {field("Certifications", s.certifications)}
            {field("Insurance Information", s.insurance_info, "supplier-field-insurance")}
          </div>
        </Card>
        <Card title="Commercial Terms" testid="supplier-terms-card">
          <div className="grid sm:grid-cols-2 gap-4">
            {field("MOQ", s.moq)}
            {field("Lead Time", s.lead_time)}
            {field("Payment Terms", s.payment_terms)}
            {field("Shipping Terms", s.shipping_terms)}
            {field("Warranty", s.warranty)}
            {field("Last Contacted", s.last_contacted)}
          </div>
        </Card>
        <Documents s={s} onChange={load} />
        <Card title="Internal Notes" testid="supplier-notes-card">
          <p className="text-[13.5px] text-[var(--pg-navy)] whitespace-pre-wrap">{s.notes || <span className="text-slate-300">No internal notes.</span>}</p>
        </Card>
        <Card title={`Approval History (${(s.approval_history || []).length})`} testid="supplier-history-card">
          {(s.approval_history || []).length === 0 && <p className="text-[13px] text-slate-400">No review actions recorded yet.</p>}
          <ul className="space-y-2.5">
            {[...(s.approval_history || [])].reverse().map((h, i) => (
              <li key={`${h.at}-${i}`} className="text-[13px] flex flex-wrap gap-x-2 items-baseline" data-testid={`supplier-history-${i}`}>
                <span className="font-[Poppins] font-semibold text-[var(--pg-navy)] capitalize">{(h.action || "").replace(/_/g, " ")}</span>
                {h.from && <span className="text-slate-400">{h.from} → {h.to}</span>}
                <span className="text-slate-500">by {h.by}</span>
                <span className="text-slate-400">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                {h.note && <span className="w-full text-[12.5px] text-slate-500 italic">"{h.note}"</span>}
              </li>
            ))}
          </ul>
        </Card>
        <Card title={`Audit Trail (${(s.audit || []).length})`} testid="supplier-audit-card">
          {(s.audit || []).length === 0 && <p className="text-[13px] text-slate-400">No audit entries yet.</p>}
          <ul className="space-y-2.5">
            {(s.audit || []).slice(0, 20).map((a) => (
              <li key={a.id} className="text-[13px]" data-testid={`supplier-audit-${a.id}`}>
                <AuditBadge action={a.action} /> <span className="text-slate-500">{a.details}</span>
                <div className="text-[11.5px] text-slate-400">{a.actor} · {(a.created_at || "").slice(0, 16).replace("T", " ")}</div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
